/**
 * WorkKanbanView — Kanban de Execucao Operacional (alta densidade)
 *
 * 4 colunas: Pendente | Em Andamento | Aguardando | Concluido
 * Drag-drop SOMENTE dentro da mesma coluna (reordenacao de prioridade).
 * Densidade global (1-4 cards por linha) controlada via prop.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock, Play, Pause, CheckCircle, AlertCircle, Ghost, User } from 'lucide-react';
import type { UnifiedWorkItem, WorkItemStatus } from '../types/workItem';
import { WORK_SOURCE_LABELS, WORK_SOURCE_COLORS } from '../types/workItem';

// --- Types ---

export type CardDensity = 1 | 2 | 3 | 4 | 5;

// --- Column definitions ---

interface ColumnDef {
  id: WorkItemStatus;
  title: string;
  icon: React.FC<{ size?: number; className?: string }>;
  borderColor: string;
  wipLimit?: number;
}

const COLUMNS: ColumnDef[] = [
  { id: 'pending', title: 'Pendente', icon: Clock, borderColor: 'border-t-amber-400' },
  { id: 'in_progress', title: 'Em Andamento', icon: Play, borderColor: 'border-t-blue-400' },
  { id: 'waiting', title: 'Aguardando', icon: Pause, borderColor: 'border-t-slate-400' },
  { id: 'completed', title: 'Concluido', icon: CheckCircle, borderColor: 'border-t-emerald-400' },
];

const CARD_HEIGHT: Record<CardDensity, number> = { 1: 96, 2: 76, 3: 56, 4: 48, 5: 32 };

// --- SLA helpers ---

function getSlaRemainingMinutes(item: UnifiedWorkItem): number | null {
  const deadline = item.source_context?.sla_deadline ?? item.due_date;
  if (!deadline) return null;
  return (new Date(deadline).getTime() - Date.now()) / 60000;
}

function getSlaStatus(item: UnifiedWorkItem): 'violated' | 'warning' | 'normal' {
  const mins = getSlaRemainingMinutes(item);
  if (mins === null) return 'normal';
  if (mins < 0) return 'violated';
  if (mins < 120) return 'warning';
  return 'normal';
}

function formatSlaShort(item: UnifiedWorkItem): string | null {
  const deadline = item.source_context?.sla_deadline ?? item.due_date;
  if (!deadline) return null;
  const diffMin = (new Date(deadline).getTime() - Date.now()) / 60000;
  const absDiff = Math.abs(diffMin);
  const sign = diffMin < 0 ? '-' : '';
  if (absDiff < 60) return `${sign}${Math.round(absDiff)}m`;
  if (absDiff < 1440) return `${sign}${Math.round(absDiff / 60)}h`;
  return `${sign}${Math.round(absDiff / 1440)}d`;
}

function sortBySla(items: UnifiedWorkItem[]): UnifiedWorkItem[] {
  return [...items].sort((a, b) => {
    const slaA = getSlaRemainingMinutes(a);
    const slaB = getSlaRemainingMinutes(b);
    if (slaA !== null && slaB !== null) return slaA - slaB;
    if (slaA !== null) return -1;
    if (slaB !== null) return 1;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
}

function avgSlaMinutes(items: UnifiedWorkItem[]): number | null {
  const vals = items.map(getSlaRemainingMinutes).filter((v): v is number => v !== null);
  if (vals.length === 0) return null;
  return vals.reduce((sum, v) => sum + v, 0) / vals.length;
}

function formatAvgSla(mins: number | null): string {
  if (mins === null) return '\u2014';
  const abs = Math.abs(mins);
  const sign = mins < 0 ? '-' : '';
  if (abs < 60) return `${sign}${Math.round(abs)}m`;
  if (abs < 1440) return `${sign}${(abs / 60).toFixed(1)}h`;
  return `${sign}${(abs / 1440).toFixed(1)}d`;
}

// --- Compact card ---

const WorkCard = React.memo<{
  item: UnifiedWorkItem;
  height: number;
  isDraggedOver: boolean;
  onDragStart: (e: React.DragEvent, id: string, status: string) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onClick?: (id: string) => void;
  dragging: boolean;
}>(({ item, height, isDraggedOver, onDragStart, onDragEnd, onDragOver, onClick, dragging }) => {
  const slaStatus = getSlaStatus(item);
  const slaLabel = formatSlaShort(item);
  const isOverdue = item.is_overdue || slaStatus === 'violated';
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const didDragRef = useRef(false);
  const isCompact = height <= 48;
  const showMeta = height >= 56;

  const borderLeft = slaStatus === 'violated'
    ? 'border-l-[3px] border-l-red-600 bg-red-50/60 dark:bg-red-950/20'
    : slaStatus === 'warning'
      ? 'border-l-[3px] border-l-amber-500 bg-amber-50/40 dark:bg-amber-950/15'
      : 'border-l-2 border-l-transparent';

  const priorityBadge = (() => {
    switch ((item.priority ?? '').toLowerCase()) {
      case 'critica': return { label: 'CRI', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' };
      case 'alta': return { label: 'ALT', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
      default: return null;
    }
  })();

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    didDragRef.current = false;
  }, []);

  const handleDragStartWrapper = useCallback((e: React.DragEvent) => {
    didDragRef.current = true;
    onDragStart(e, item.id, item.status);
  }, [onDragStart, item.id, item.status]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (didDragRef.current) { didDragRef.current = false; dragStartPos.current = null; return; }
    if (dragStartPos.current) {
      const dx = Math.abs(e.clientX - dragStartPos.current.x);
      const dy = Math.abs(e.clientY - dragStartPos.current.y);
      dragStartPos.current = null;
      if (dx > 5 || dy > 5) return;
    }
    onClick?.(item.source_id);
  }, [onClick, item.source_id]);

  return (
    <>
      {isDraggedOver && (
        <div className="mx-1 h-0.5 rounded bg-blue-400 dark:bg-blue-500 transition-all" />
      )}
      <div
        draggable
        onDragStart={handleDragStartWrapper}
        onDragEnd={onDragEnd}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e, item.id); }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        title={item.title}
        style={{ height, minHeight: height, maxHeight: height }}
        className={`${borderLeft} flex ${isCompact ? 'items-center gap-2' : 'flex-col justify-center gap-0.5'} overflow-hidden rounded border border-slate-200 bg-white px-2 select-none dark:border-slate-700/60 dark:bg-slate-800/60 cursor-pointer hover:bg-blue-50/60 hover:border-blue-300 hover:shadow-sm dark:hover:bg-blue-950/20 dark:hover:border-blue-600 active:scale-[0.99] ${dragging ? 'opacity-30 shadow-lg ring-2 ring-blue-300/40 scale-[1.02]' : 'opacity-100'} transition-all duration-100`}
      >
        {isCompact ? (
          /* Density 4-5: single row */
          <>
            <span className={`shrink-0 rounded px-1 py-px text-[9px] font-semibold leading-tight ${WORK_SOURCE_COLORS[item.source]}`}>
              {WORK_SOURCE_LABELS[item.source].slice(0, 3)}
            </span>
            <span className="min-w-0 flex-1 truncate text-[11px] font-medium leading-tight text-slate-800 dark:text-slate-200">
              {item.title}
            </span>
            <div className="flex shrink-0 items-center gap-1">
              {priorityBadge && (
                <span className={`rounded px-1 py-px text-[8px] font-bold ${priorityBadge.cls}`}>{priorityBadge.label}</span>
              )}
              {slaLabel && (
                <span className={`font-mono text-[9px] font-bold tabular-nums ${slaStatus === 'violated' ? 'text-red-600 dark:text-red-400' : slaStatus === 'warning' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400 dark:text-slate-500'}`}>
                  {slaLabel}
                </span>
              )}
            </div>
          </>
        ) : (
          /* Density 1-3: multi-line */
          <>
            {/* Row 1: source + title + priority */}
            <div className="flex items-center gap-1.5 min-w-0">
              <span className={`shrink-0 rounded px-1 py-px text-[9px] font-semibold leading-tight ${WORK_SOURCE_COLORS[item.source]}`}>
                {WORK_SOURCE_LABELS[item.source].slice(0, 3)}
              </span>
              <span className="min-w-0 flex-1 truncate text-[11px] font-semibold leading-tight text-slate-900 dark:text-slate-100">
                {item.title}
              </span>
              {priorityBadge && (
                <span className={`shrink-0 rounded px-1 py-px text-[8px] font-bold ${priorityBadge.cls}`}>{priorityBadge.label}</span>
              )}
            </div>
            {/* Row 2: stage + executor + SLA */}
            {showMeta && (
              <div className="flex items-center gap-2 min-w-0 font-mono text-[9px] tracking-wide text-slate-500 dark:text-slate-400">
                {item.source_context?.stage_name && (
                  <span className="truncate max-w-[40%]">{item.source_context.stage_name}</span>
                )}
                <span className="flex items-center gap-0.5 truncate max-w-[30%]">
                  <User size={8} className="shrink-0 opacity-60" />
                  {item.assigned_to_name || <span className="italic text-amber-500">Sem resp.</span>}
                </span>
                <div className="flex-1" />
                {isOverdue && slaLabel && (
                  <span className="flex items-center gap-0.5 rounded bg-red-100 px-1 py-px text-[9px] font-bold tabular-nums text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    <AlertCircle size={8} />
                    {slaLabel}
                  </span>
                )}
                {!isOverdue && slaLabel && (
                  <span className={`tabular-nums font-bold ${slaStatus === 'warning' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400 dark:text-slate-500'}`}>
                    {slaLabel}
                  </span>
                )}
              </div>
            )}
            {/* Row 3: SLA mini bar (density 1-2 only) */}
            {height >= 72 && slaLabel && (
              <div className="w-full h-1 rounded-sm bg-slate-200 dark:bg-slate-700 overflow-hidden">
                <div className={`h-full transition-all ${slaStatus === 'violated' ? 'bg-red-500' : slaStatus === 'warning' ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(Math.max(100 - (getSlaRemainingMinutes(item) ?? 0) / 10, 0), 100)}%` }} />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
});
WorkCard.displayName = 'WorkCard';

// --- Main component ---

interface WorkKanbanViewProps {
  items: UnifiedWorkItem[];
  density: CardDensity;
  onCardClick?: (itemId: string) => void;
}

export const WorkKanbanView: React.FC<WorkKanbanViewProps> = React.memo(({ items, density, onCardClick }) => {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [draggedFromColumn, setDraggedFromColumn] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [manualOrder, setManualOrder] = useState<Record<string, string[]>>({});

  const cardHeight = CARD_HEIGHT[density];

  const columnData = useMemo(() => {
    const grouped: Record<string, UnifiedWorkItem[]> = {};
    for (const col of COLUMNS) grouped[col.id] = [];
    for (const item of items) {
      if (grouped[item.status]) grouped[item.status].push(item);
    }
    for (const key of Object.keys(grouped)) {
      const manual = manualOrder[key];
      if (manual && manual.length > 0) {
        const lookup = new Map(grouped[key].map((it) => [it.id, it]));
        const ordered: UnifiedWorkItem[] = [];
        for (const id of manual) {
          const it = lookup.get(id);
          if (it) { ordered.push(it); lookup.delete(id); }
        }
        grouped[key] = [...ordered, ...sortBySla([...lookup.values()])];
      } else {
        grouped[key] = sortBySla(grouped[key]);
      }
    }
    return grouped;
  }, [items, manualOrder]);

  const prevItemIdsRef = useRef<Record<string, string>>({});
  useEffect(() => {
    const sigs: Record<string, string> = {};
    for (const col of COLUMNS) {
      sigs[col.id] = items.filter((i) => i.status === col.id).map((i) => i.id).sort().join(',');
    }
    for (const col of COLUMNS) {
      if (prevItemIdsRef.current[col.id] && prevItemIdsRef.current[col.id] !== sigs[col.id]) {
        setManualOrder((prev) => {
          if (!prev[col.id]) return prev;
          const next = { ...prev };
          delete next[col.id];
          return next;
        });
      }
    }
    prevItemIdsRef.current = sigs;
  }, [items]);

  const handleDragStart = useCallback((e: React.DragEvent, id: string, status: string) => {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(id);
    setDraggedFromColumn(status);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDraggedFromColumn(null);
    setDropTargetId(null);
  }, []);

  const handleCardDragOver = useCallback((_e: React.DragEvent, targetId: string) => {
    setDropTargetId(targetId);
  }, []);

  const handleColumnDragOver = useCallback((e: React.DragEvent, columnId: string) => {
    if (draggedFromColumn !== columnId) { e.dataTransfer.dropEffect = 'none'; return; }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, [draggedFromColumn]);

  const handleColumnDrop = useCallback((e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    if (!draggedId || draggedFromColumn !== columnId) return;
    const colItems = columnData[columnId];
    if (!colItems) return;
    const currentIds = colItems.map((i) => i.id);
    const fromIdx = currentIds.indexOf(draggedId);
    if (fromIdx === -1) return;
    let toIdx = dropTargetId ? currentIds.indexOf(dropTargetId) : currentIds.length - 1;
    if (toIdx === -1) toIdx = currentIds.length - 1;
    if (fromIdx !== toIdx) {
      const reordered = [...currentIds];
      reordered.splice(fromIdx, 1);
      reordered.splice(toIdx, 0, draggedId);
      setManualOrder((prev) => ({ ...prev, [columnId]: reordered }));
    }
    setDraggedId(null);
    setDraggedFromColumn(null);
    setDropTargetId(null);
  }, [draggedId, draggedFromColumn, dropTargetId, columnData]);

  return (
    <div className="flex h-full w-full gap-2 px-3 py-1.5">
      {COLUMNS.map((col) => {
        const colItems = columnData[col.id] ?? [];
        const avg = avgSlaMinutes(colItems);
        const isInvalidDrop = draggedId !== null && draggedFromColumn !== col.id;

        return (
          <div
            key={col.id}
            className={`flex min-w-[260px] flex-1 flex-col rounded border transition-all duration-100 ${isInvalidDrop ? 'border-slate-200/40 opacity-40' : 'border-slate-200 dark:border-slate-700/60'} bg-slate-50/60 dark:bg-slate-800/20`}
            onDragOver={(e) => handleColumnDragOver(e, col.id)}
            onDrop={(e) => handleColumnDrop(e, col.id)}
          >
            <div className={`shrink-0 border-t-[3px] px-2.5 py-1 ${col.borderColor}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <col.icon size={11} className="text-slate-400 dark:text-slate-500" />
                  <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                    {col.title}
                  </span>
                  <span className="rounded bg-slate-200/80 px-1.5 py-px font-mono text-[9px] font-bold tabular-nums text-slate-600 dark:bg-slate-700/60 dark:text-slate-400">
                    {colItems.length}
                  </span>
                  {col.wipLimit != null && colItems.length > col.wipLimit && (
                    <span className="rounded bg-red-100 px-1 py-px text-[8px] font-bold text-red-600 dark:bg-red-900/30 dark:text-red-400">WIP</span>
                  )}
                </div>
                {avg !== null && (
                  <span className={`font-mono text-[9px] tabular-nums ${avg < 0 ? 'text-red-500' : avg < 120 ? 'text-amber-500' : 'text-slate-400 dark:text-slate-500'}`}>
                    SLA {formatAvgSla(avg)}
                  </span>
                )}
              </div>
            </div>

            <div
              className="flex-1 overflow-y-auto p-1.5"
              style={{
                display: 'grid',
                gridTemplateColumns: density === 1 || density === 5 ? '1fr' : `repeat(${density}, minmax(0, 1fr))`,
                gap: 4,
                alignContent: 'start',
              }}
            >
              {colItems.length === 0 ? (
                <div className="col-span-full flex h-12 flex-col items-center justify-center rounded border border-dashed border-slate-300 text-slate-400 dark:border-slate-600 dark:text-slate-500">
                  <Ghost size={14} className="opacity-40" />
                  <span className="text-[9px]">Vazio</span>
                </div>
              ) : (
                colItems.map((item) => (
                  <WorkCard
                    key={item.id}
                    item={item}
                    height={cardHeight}
                    isDraggedOver={dropTargetId === item.id && draggedId !== item.id && draggedFromColumn === col.id}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleCardDragOver}
                    onClick={onCardClick}
                    dragging={draggedId === item.id}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
});

WorkKanbanView.displayName = 'WorkKanbanView';
